/*
 *  -----------------------------------------------------------------------
 *    <copyright file="generate-report-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Errors } from '@components/central-monitoring/panels/add-panel/interfaces';
import Button from '@components/core/buttons';
import CharactersRemaining from '@components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import FormControl from '@components/core/form-control';
import Loading from '@components/core/loading';
import SelectWithCategories from '@components/core/select-with-categories';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import TextField from '@components/core/text-field';
import {
    formGeneratedReportSubmitData,
    formRiskReviewList,
    formSignatoryGridItems,
    getDataSetsList,
    sortDataSets,
} from '@components/shared/generate-report-dialog/generate-report-dialog.helper';
import {
    GenerateReportDialogProps,
    IReportFormInput,
} from '@components/shared/generate-report-dialog/generate-report-dialog.interface';
import SignatoriesEditableGrid from '@components/shared/signatories-editable-grid';
import { SignatoryGridItem } from '@components/shared/signatories-editable-grid/signatories-editable-grid.interface';
import { IReportInitialState } from './generate-report-dialog.data';
import { useBoolean, useLocalState, useTranslation } from '@hooks/index';
import Constants, { MODAL_CLOSE_ACTIONS } from '@utils/constants';
import NotificationService from '@utils/notification';
import { validateFields } from '@utils/validation';
import { GET_DATASETS } from '@queries/central-monitoring';
import { dataSets, dataSetsVariables } from '@queries/generated/central-monitoring.generated';
import { DataSetFields } from '@queries/generated/fragments.generated';
import './styles.scss';
import { useGetRiskReviewsList, useMutationGeneratedReport } from '@apis/bff-api/report-templates';

const GenerateReportDialog = ({ template, latestReport, module, orgKey, onClose }: GenerateReportDialogProps) => {
    const { t } = useTranslation();
    const localState = useLocalState();

    const [comparisonDataSets, setComparisonDatasets] = useState<SelectWithSearchDataArrayInterface[]>([]);
    const [signatories, setSignatories] = useState<SignatoryGridItem[]>(
        formSignatoryGridItems(latestReport?.signatories ?? []),
    );
    const [signatoriesErrors, setSignatoriesErrors] = useState<Errors>({});

    const showAddNewSignatory = useBoolean(false);

    const studyId = localState.state?.selectedStudy?.id;
    const studyNumber = localState.state?.selectedStudy?.studyNumber;

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors, isDirty },
    } = useForm<IReportFormInput>({
        mode: 'all',
        defaultValues: { ...IReportInitialState },
    });

    const datasetIdFieldValue = watch('datasetId');
    const isDefaultDatasetSelected = !datasetIdFieldValue || datasetIdFieldValue === 'default';

    const { data: dataSetsData, loading: dataSetsLoading } = useQuery<dataSets, dataSetsVariables>(GET_DATASETS, {
        variables: { studyId },
    });
    const { data: riskReviewsData, isLoading: riskReviewsLoading } = useGetRiskReviewsList(studyId);
    const { trigger: putGeneratedReport, isMutating } = useMutationGeneratedReport();

    const riskReviews = formRiskReviewList(orderBy(riskReviewsData ?? [], [{ field: 'startDate', dir: 'desc' }]));

    const allDataSets = getDataSetsList(
        sortDataSets((dataSetsData?.dataSets?.filter((item) => item?.active === true) as DataSetFields[]) ?? []),
    );
    const isCMModule = module === Constants.Modules.CM;

    const validate = (value: string, field: string, length?: number, required = false) => {
        const error: Errors = validateFields(value, field, length, required);

        return error[field];
    };

    const onDatasetChange = (e: DropDownListChangeEvent, onChange: (...event: any[]) => void) => {
        setValue('comparisonDatasetId', 'default');
        const selectedDataset = allDataSets.find((dataSet) => dataSet.id === e.target.value)!;
        const availableDatasets = allDataSets.filter((set) => {
            if (set?.id === 'default') return true;
            return (
                selectedDataset.text &&
                set.text &&
                new Date(selectedDataset.text).getTime() > new Date(set.text).getTime()
            );
        });
        setComparisonDatasets(availableDatasets);
        onChange(e.target.value);
    };

    const checkSignatoriesErrors = () => {
        const errs: Errors = {};
        signatories.forEach((signatory) => {
            if (!signatory.signatory) {
                errs[`signatory-${signatory.index}`] = t('ra:requiredFieldError');
            }
        });
        if (Object.keys(errs).length) {
            setSignatoriesErrors({ ...signatoriesErrors, ...errs });
        }

        return !!Object.keys(errs).length;
    };

    const onSubmit: SubmitHandler<Partial<IReportFormInput>> = (data) => {
        if (checkSignatoriesErrors()) {
            return;
        }

        putGeneratedReport(
            formGeneratedReportSubmitData(data, signatories, template.id!, orgKey, studyId, module, studyNumber),
        )
            .then(() => {
                onClose(MODAL_CLOSE_ACTIONS.SUCCESS);
                NotificationService.showNotification(t('reports:reportSuccessfulCreated'));
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    useEffect(() => {
        if (
            module === Constants.Modules.RAM &&
            !template.includeReviews &&
            !template.includeNameAndVersion &&
            !template.includeSignatures
        ) {
            onSubmit({ onlyIncludeReviewed: false });
        }
    }, []);

    return (
        <CustomDialog
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open
            className={cn('custom-width-popup generate-report-dialog max-w700', {
                'k-d-none': showAddNewSignatory.value,
            })}
        >
            <form id="generate-report-form" className="generate-report-form" onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle id="generate-report-dialog-title">
                    {t('reports:generateReport')}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => onClose()}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent className="generate-report-dialog-content">
                    {(dataSetsLoading || riskReviewsLoading || isMutating) && <Loading float />}
                    {isCMModule && (
                        <>
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type">
                                    <Controller
                                        control={control}
                                        name="onlyIncludeReviewed"
                                        render={({ field: { onChange, value } }) => (
                                            <Checkbox
                                                rounded={null}
                                                id="only-reviewed-sites-checkbox"
                                                checked={value}
                                                onChange={onChange}
                                                label={t('cm:onlyIncludeReviewedSites')}
                                                data-testid="only-reviewed-sites-checkbox"
                                                size="medium"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper !k-mb-0">
                                <FormControl variant="outlined" className="form-control-type not-full mr">
                                    <Controller
                                        control={control}
                                        name="datasetId"
                                        rules={{
                                            required: {
                                                value: true,
                                                message: t('ra:requiredFieldError'),
                                            },
                                        }}
                                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                                            <SelectWithCategories
                                                label={`${t('cm:dataset')}*`}
                                                dataArray={allDataSets}
                                                onChange={() => (e: DropDownListChangeEvent) =>
                                                    onDatasetChange(e, onChange)
                                                }
                                                fieldSelect="datasetId"
                                                currentState={value}
                                                error={error?.message}
                                                filterable={false}
                                                labelField="dataset"
                                                customClassName="dataset-select"
                                            />
                                        )}
                                    />
                                </FormControl>
                                {template.includeComparisonDataset && (
                                    <FormControl variant="outlined" className="form-control-type not-full">
                                        <Controller
                                            control={control}
                                            name="comparisonDatasetId"
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <SelectWithCategories
                                                    label={t('cm:comparisonDataset')}
                                                    dataArray={comparisonDataSets}
                                                    onChange={() => onChange}
                                                    fieldSelect="comparisonDatasetId"
                                                    currentState={value}
                                                    error={error?.message}
                                                    filterable={false}
                                                    labelField="comparisonDataset"
                                                    customClassName="dataset-select"
                                                    disabled={!getValues('datasetId')}
                                                    initialState=""
                                                />
                                            )}
                                        />
                                    </FormControl>
                                )}
                            </div>
                        </>
                    )}
                    {template.includeReviews && (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <Controller
                                    control={control}
                                    name="riskReviewId"
                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                        <SelectWithCategories
                                            label={t('ra:riskReview')}
                                            dataArray={riskReviews}
                                            onChange={() => onChange}
                                            fieldSelect="riskReview"
                                            currentState={value}
                                            error={error?.message}
                                            filterable={false}
                                            labelField="riskReview"
                                            customClassName="risk-review-select"
                                            searchable
                                        />
                                    )}
                                />
                            </FormControl>
                        </div>
                    )}
                    {template.includeNameAndVersion && (
                        <div className="type-edc-wrapper !k-mb-4">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <Controller
                                    control={control}
                                    name="reportName"
                                    rules={{
                                        validate: {
                                            maxLength: (value) => validate(value, 'reportName', 50) ?? undefined,
                                        },
                                    }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <TextField
                                            label={t('cm:reportName')}
                                            id="report-name"
                                            fullWidth
                                            value={value}
                                            onChange={onChange}
                                            placeholder={t('cm:enterReportName')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="report-name-limit"
                                                    length={value?.length}
                                                    max={50}
                                                    error={error?.message}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!error?.message}
                                            className="opra-text-input"
                                        />
                                    )}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <Controller
                                    control={control}
                                    name="reportVersion"
                                    rules={{
                                        validate: {
                                            maxLength: (value) => validate(value, 'reportVersion', 10) ?? undefined,
                                        },
                                    }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <TextField
                                            label={t('cm:reportVersion')}
                                            id="report-version"
                                            fullWidth
                                            value={value}
                                            onChange={onChange}
                                            placeholder={t('cm:enterReportVersion')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="report-version-limit"
                                                    length={value?.length}
                                                    max={10}
                                                    error={error?.message}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!error?.message}
                                            className="opra-text-input"
                                        />
                                    )}
                                />
                            </FormControl>
                        </div>
                    )}
                    {template.includeSignatures && (
                        <SignatoriesEditableGrid
                            signatories={signatories}
                            setSignatories={setSignatories}
                            signatoriesErrors={signatoriesErrors}
                            setSignatoriesErrors={setSignatoriesErrors}
                            studyId={studyId}
                            module={module}
                            showAddNewSignatory={showAddNewSignatory}
                        />
                    )}
                </DialogContent>
                <DialogActions className="!k-px-5 !k-pb-7.5 !k-pt-0">
                    <Button
                        disabled={
                            Object.keys(errors).length !== 0 ||
                            (isCMModule && isDefaultDatasetSelected) ||
                            (!isDirty && isCMModule) ||
                            isMutating
                        }
                        text={t('common:Generate')}
                        id="generate-report-submit"
                        data-testid="submit-button"
                        type="submit"
                    />
                </DialogActions>
            </form>
        </CustomDialog>
    );
};

export default GenerateReportDialog;
